<template lang="pug">
#send-document-category-list(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 文書種類修正
    .box-body
      CategoryTable(v-model="items" :fields="fields" :isLoading="isLoading")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showUpdateModal" type="button") 更新
        button.btn.cancel-btn(@click="gotoCategoryList" type="button") キャンセル

  OkCancelModal(v-model="showCategoryUpdateModal" title="文書種類を更新しますか？" @ok="updateCategory")
  OkModal(v-model="showCategoryUpdateSuccessModal" title="更新に成功しました。" @ok="gotoCategoryList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import SendDocumentCategoriesService, { SendDocumentCategoryGetModel, SendDocumentCategoryPatchModel } from "@/commons/services/SendDocumentCategories";

import CategoryTable, { CategoryTableField, CategoryTableItem } from "@/pc/containers/sendDocuments/CategoryTable.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";

/**
 * ### 文書送付一覧画面コンポーネント
 */
@Component({
  components: {
    CategoryTable,
    PageLoading,
    OkCancelModal,
    OkModal,
    ValidationAlert
  },
  $_veeValidate: { validator: "new" }
})
export default class CategoryEdit extends Vue {
  /** 文書送付カテゴリーサービス */
  @Inject("SendDocumentCategoriesService") categoriesService!: SendDocumentCategoriesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showCategoryUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showCategoryUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** テーブル列名 */
  fields: Array<CategoryTableField> = [];
  /** テーブル各行の情報 */
  items: Array<CategoryTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<CategoryTableField> = this.createFields();
    this.fields = fields;

    // 動的に変更する画面情報を作成する
    this.items = [];
    await this.createView();

    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<CategoryTableField> {
    let fields: Array<CategoryTableField> = [];

    fields.push({ key: "editNo", label: "番号", sortable: false, sortDirection: "desc", class: "edit-no" });
    fields.push({ key: "editEnabled", label: "使用", sortable: false, sortDirection: "desc", class: "edit-enabled text-center" });
    fields.push({ key: "editName", label: "種類名", sortable: false, sortDirection: "desc", class: "edit-name" });
    fields.push({ key: "editDescription", label: "説明", sortable: false, sortDirection: "desc", class: "edit-description" });

    return fields;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // テーブルに渡す各プロパティを作成する
    const sendDocuments: Array<SendDocumentCategoryGetModel> = await this.getSendDocumentCategories();
    const items: Array<CategoryTableItem> = this.createItems(sendDocuments);
    this.items = this.items.concat(items);

    this.isLoading = false;

    return;
  }

  /**
   * ### 文書送付カテゴリーを取得する
   */
  public async getSendDocumentCategories(): Promise<Array<SendDocumentCategoryGetModel>> {
    const response: Array<SendDocumentCategoryGetModel> = await this.categoriesService.gets(userStore);

    return response;
  }

  /**
   * ### テーブル行を作成する
   */
  public createItems(categories: Array<SendDocumentCategoryGetModel>): Array<CategoryTableItem> {
    let items: Array<CategoryTableItem> = [];

    for (const category of categories) {
      items.push(category);
    }

    return items;
  }

  /**
   * ### 更新確認ダイアログを表示する
   */
  public async showUpdateModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showCategoryUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  public async updateCategory(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      for (const item of this.items) {
        const requestBody: SendDocumentCategoryPatchModel = {
          name: item.name,
          description: item.description,
          enabled: item.enabled,
          updated: item.updated
        };
        await this.categoriesService.patch(userStore, item.id, requestBody);
      }

      this.showCategoryUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 文書送付カテゴリー一覧画面へ遷移する
   */
  public gotoCategoryList(): void {
    this.$router.push(PcUrl.SendDocumentCategoryList);
    return;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#send-document-category-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
